import React, { useEffect, useMemo, useState } from "react";
import api from "../services/api";

/** 管理一覧の「送信済み」から除外し、下段に回す自動通知タイトル（docs/05_worktime.md） */
const AUTO_NOTIFICATION_TITLES = new Set(["スケジュール登録のお願い"]);

const AdminNotifications = () => {
  const [notifications, setNotifications] = useState([]);
  const [selected, setSelected] = useState(null); 
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");

  const fetchNotifications = async () => {
    const res = await api.get("/api/admin/notifications/");
    setNotifications(Array.isArray(res.data) ? res.data : []);
  };

  const { manualNotifications, autoNotifications } = useMemo(() => {
    const list = notifications || [];
    const manual = [];
    const auto = [];
    for (const n of list) {
      if (AUTO_NOTIFICATION_TITLES.has(n.title)) auto.push(n);
      else manual.push(n);
    }
    return { manualNotifications: manual, autoNotifications: auto };
  }, [notifications]);

  const fetchDetail = async (id) => {
    try {
      const res = await api.get(`/api/admin/notifications/${id}/`);
      setSelected(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  const handleSend = async () => {
    if (!title || !message) return alert("タイトルを入力してください。");
    setLoading(true);
    try {
      await api.post("/api/admin/notifications/create/", { title, message });
      alert("通知を送信しました。");
      setTitle("");
      setMessage("");
      fetchNotifications();
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("削除してもよろしいですか？")) return;
    await api.delete(`/api/admin/notifications/${id}/delete/`);
    fetchNotifications();
  };

  return (
    <div style={{ padding: 20 }}>
      <h2>管理者通知</h2>

      {/* --- お知らせを送る --- */}
      <div style={{ marginBottom: 30, background: "#f8f9fa", padding: 15, borderRadius: 8 }}>
        <h4>新規お知らせを送信</h4>
        <input
          type="text"
          placeholder="タイトル"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ width: "100%", marginBottom: 10, padding: 8 }}
        />
        <textarea
          placeholder="本文"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          style={{ width: "100%", padding: 8 }}
        />
        <button onClick={handleSend} disabled={loading} style={{ marginTop: 10 }}>
          {loading ? "送信中..." : "送信"}
        </button>
      </div>

      {/* --- お知らせリスト（手動・一般） --- */}
      <h4>送信済み通知</h4>
      {manualNotifications.map((n) => (
        <div key={n.id} style={{ border: "1px solid #ccc", padding: 10, borderRadius: 6, marginBottom: 10 }}>
          <strong>{n.title}</strong>
          <p>{n.message}</p>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={() => fetchDetail(n.id)}>詳細を見る</button>
            <button onClick={() => handleDelete(n.id)} style={{ background: "red", color: "white" }}>
              削除
            </button>
          </div>
        </div>
      ))}

      {autoNotifications.length > 0 && (
        <>
          <h4 style={{ marginTop: 28 }}>自動送信（スケジュール催促など）</h4>
          <p style={{ color: "#64748b", fontSize: "0.9rem" }}>
            一覧の主ブロックには出さず、参照用に表示します。
          </p>
          {autoNotifications.map((n) => (
            <div
              key={n.id}
              style={{ border: "1px solid #e2e8f0", padding: 10, borderRadius: 6, marginBottom: 10, background: "#f8fafc" }}
            >
              <strong>{n.title}</strong>
              <p>{n.message}</p>
              <div style={{ display: "flex", gap: 10 }}>
                <button type="button" onClick={() => fetchDetail(n.id)}>
                  閲覧状況
                </button>
                <button type="button" onClick={() => handleDelete(n.id)} style={{ background: "red", color: "white" }}>
                  削除
                </button>
              </div>
            </div>
          ))}
        </>
      )}

      {/* --- 詳細情報 --- */}
      {selected && (
        <div style={{ marginTop: 30, background: "#fff", padding: 15, borderRadius: 8 }}>
          <h3> {selected.title} の詳細</h3>
          <p>{selected.message}</p>
          <h4>閲覧状況:</h4>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#eee" }}>
                <th style={{ border: "1px solid #ccc", padding: 5 }}>社員番号</th>
                <th style={{ border: "1px solid #ccc", padding: 5 }}>氏名</th>
                <th style={{ border: "1px solid #ccc", padding: 5 }}>部署名</th>
                <th style={{ border: "1px solid #ccc", padding: 5 }}>役職</th>
                <th style={{ border: "1px solid #ccc", padding: 5 }}>閲覧済み</th>
                <th style={{ border: "1px solid #ccc", padding: 5 }}>閲覧日時</th>
              </tr>
            </thead>
            <tbody>
              {selected.readers.map((r) => (
                <tr key={r.id}>
                  <td style={{ border: "1px solid #ccc", padding: 5 }}>{r.employee.employeeId}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5 }}>{r.employee.name}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5 }}>{r.employee.departmentName}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5 }}>{r.employee.position}</td>
                  <td style={{ border: "1px solid #ccc", padding: 5, textAlign: "center" }}>
                    {r.is_read ? "✓" : "✗"}
                  </td>
                  <td style={{ border: "1px solid #ccc", padding: 5 }}>
                    {r.read_at ? new Date(r.read_at).toLocaleString() : "-"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => setSelected(null)} style={{ marginTop: 15 }}>
            閉じる
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
